import React, { useEffect, useState } from "react";
import { get, post } from "../../services/api";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function SessionSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      createdBy: localStorage.getItem("userId"),
      name: "",
      startDate: "",
      endDate: "",
      description: "",
      organizationId: organizationId,
    },
  });

  const [sessionsData, setSessionsData] = useState([]);

  const getSessionsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Session/GetSessions?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSessionsData(response?.sessions);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };

  useEffect(() => {
    getSessionsList();
  }, [triggerRerender]);

  const handleEdit = async (row) => {
    try {
      const response = await get(`Session/GetSessionById?sessionId=${row.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const sessionData = response?.session;

      reset({
        ...sessionData,
        startDate: sessionData.startDate.split("T")[0],
        endDate: sessionData.endDate.split("T")[0],
        id: sessionData.id,
        updatedBy: localStorage.getItem("userId"),
      });
    } catch (error) {
      console.error("Error editing section:", error);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        Cell: ({ cell }) => cell.getValue()?.split("T")[0],
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        Cell: ({ cell }) => cell.getValue()?.split("T")[0],
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original)}
            >
              <i className="fas fa-pencil"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = sessionsData.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      // ID: row.id,
      "Start Date": row.startDate.split("T")[0],
      "End Date": row.endDate.split("T")[0],
    }));

    console.log(filteredData);
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sessions");
    XLSX.writeFile(workbook, "Sessions.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    // Logic to determine which logo to use
    // If roleId is 2 (Organization), use orgLogo
    // If roleId is 3 (Center), use centerLogo
    const sidebarLogoUrl =
      roleId == "2" ? orgLogo : roleId == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // Add only the resolved logo
    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Sessions List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) =>
        column.accessorKey !== "actions" &&
        column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = sessionsData.map((row, index) => {
      return [
        index + 1,
        ...includedColumns.map((column) => {
          if (
            column.accessorKey === "startDate" ||
            column.accessorKey === "endDate"
          ) {
            return row[column.accessorKey]?.split("T")[0] || "";
          }
          return row[column.accessorKey] || "";
        }),
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("SessionList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post("Session/AddUpdateSession", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          createdBy: localStorage.getItem("userId"),
          name: "",
          startDate: "",
          endDate: "",
          description: "",
          organizationId: localStorage.getItem("organizationId"),
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Sessions</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSubmit(saveData)}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">
                      Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`name`, {
                        required: "This field is required.",
                        maxLength: {
                          value: 75,
                          message: "Maximum of 75 characters allowed.",
                        },
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck("name")}
                    />
                    {errors.name && (
                      <small className="text-danger">
                        {errors.name.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Start Date<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`startDate`, {
                        required: "This field is required.",
                        validate: {
                          lessThanEndDate: (value) => {
                            const endDate = getValues(`endDate`);
                            return (
                              !endDate ||
                              new Date(value) < new Date(endDate) ||
                              "Must be less than End Date."
                            );
                          },
                          // نیا validation: ایک سال سے زیادہ نہ ہو
                          maxOneYear: (value) => {
                            const endDate = getValues(`endDate`);
                            if (!endDate) return true;
                            const start = new Date(value);
                            const end = new Date(endDate);
                            const diffTime = Math.abs(end - start);
                            const diffDays = Math.ceil(
                              diffTime / (1000 * 60 * 60 * 24)
                            );
                            return (
                              diffDays <= 365 ||
                              "Session cannot be longer than one year (365 days)."
                            );
                          },
                        },
                      })}
                      type="date"
                      className="form-control"
                      placeholder=""
                    />
                    {errors.startDate && (
                      <small className="text-danger">
                        {errors.startDate.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">
                      End Date<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`endDate`, {
                        required: "This field is required.",
                        validate: {
                          greaterThanStartDate: (value) => {
                            const startDate = getValues(`startDate`);
                            return (
                              !startDate ||
                              new Date(value) > new Date(startDate) ||
                              "Must be greater than Start Date."
                            );
                          },

                          maxOneYear: (value) => {
                            const startDate = getValues(`startDate`);
                            if (!startDate) return true;
                            const start = new Date(startDate);
                            const end = new Date(value);
                            const diffTime = Math.abs(end - start);
                            const diffDays = Math.ceil(
                              diffTime / (1000 * 60 * 60 * 24)
                            );
                            return (
                              diffDays <= 365 ||
                              "Session cannot be longer than one year (365 days)."
                            );
                          },
                        },
                      })}
                      type="date"
                      className="form-control"
                      placeholder=""
                    />
                    {errors.endDate && (
                      <small className="text-danger">
                        {errors.endDate.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Description</label>
                    <input
                      {...register(`description`, {
                        maxLength: {
                          value: 250,
                          message: "Maximum of 250 characters allowed.",
                        },
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck(
                        "description"
                      )}
                    />
                    {errors.description && (
                      <small className="text-danger">
                        {errors.description.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 mt-3">
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Save" : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={sessionsData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SessionSetup;
